import * as isoCountries from 'i18n-iso-countries';
import * as enLocale from 'i18n-iso-countries/langs/en.json';
import React, { useEffect, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

isoCountries.registerLocale(enLocale);

import worldMapJson from '@/components/charts/countries.json';
import { cn } from '@/utils/classname';

interface Props {
  countries?: Record<string, number>;
  className?: string;
  highlightedClassName?: string;
  baseClassName?: string;
}

// Note: 1.6 or 8/5 is the aspect ratio of the world map
export const WorldMap = ({
  countries = {},
  className,
  highlightedClassName,
  baseClassName,
}: Props = {}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [{ width, height }, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const style = width > height * 1.6 ? { height } : { width };

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];

      setDimensions({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    resizeObserver.observe(containerRef.current);

    // Initial dimensions
    setDimensions({
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  return (
    <div
      className="flex size-full items-center justify-center overflow-hidden"
      ref={containerRef}
      data-testid="world-map"
    >
      <div style={style} className={cn('relative aspect-[8/5]', baseClassName)}>
        <ComposableMap projection="geoEqualEarth" height={500}>
          <Geographies geography={worldMapJson}>
            {({ geographies }) =>
              geographies.map(geo => {
                const alpha2Code = isoCountries.alpha3ToAlpha2(geo.id);

                const isHighlighted = alpha2Code && countries[alpha2Code];

                return (
                  <Geography
                    key={geo.id}
                    geography={geo}
                    className={cn(
                      isHighlighted
                        ? 'fill-chariot-brand-primary'
                        : 'fill-chariot-stroke',
                      isHighlighted ? highlightedClassName : className
                    )}
                  ></Geography>
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
    </div>
  );
};
